import { ArrayUtils, NarrowIndexable } from "@dtm-frontend/shared/utils";
import { Position } from "@turf/helpers";
import {
    CallbackProperty,
    Cartesian3,
    Color,
    ColorMaterialProperty,
    CustomDataSource,
    Entity,
    HeightReference,
    PointGraphics,
    PolygonHierarchy,
    PolylineGraphics,
    PositionProperty,
} from "cesium";
import { CesiumMapService } from "../../services/cesium-map.service";
import { GeoUtilsService } from "../../services/geo-utils.service";
import { DraggableHeightEntity, HeightEditable, HeightEntityType, HeightPointsProvider } from "../../services/height-helper.service";
import { checkPolygonEdgesIntersections } from "../../utils/check-polygon-edges-intersections";
import { convertSerializableCartographicToCartesian3 } from "../../utils/convert-serializable-cartographic-to-cartesian3";
import { getElevatedCesiumPoint } from "../../utils/get-elevated-cesium-point";
import { getPolygonVisualCenter } from "../../utils/get-polygon-visual-center";
import { CartographicEditPoint, CartographicEditPointParentEntity, CesiumPointWithProps } from "../cartographic-edit-point";
import { PolylineWithHeight } from "../cesium.models";
import { EditPolyline } from "../edit-polyline";
import { DEFAULT_LABEL_PROPS, EntityLabel, LabelFn, LabelProviders } from "../entity-label.model";
import { PointProps } from "../point-edit-options";
import { PolylineProps } from "../polyline-edit-options";
import { EntityStatus } from "./../entity-status.model";
import { PrismEditOptions, PrismProps } from "./prism-edit-options";

/* eslint-disable no-underscore-dangle */

export const MINIMUM_VERTEX_NUMBER = 3;

export interface PrismEditorLabelProviders extends LabelProviders {
    topHeight?: LabelFn<number>;
    bottomHeight?: LabelFn<number>;
    waypoint?: LabelFn<string>;
}

export type PrismLabel = EntityLabel<PrismEditorLabelProviders>;
type PrismLabelKey = keyof NarrowIndexable<PrismEditorLabelProviders>;

export class PrismHeightPolyline extends EditPolyline {
    private _elevatedPositions: [Cartesian3, Cartesian3] | undefined;
    private _heightForElevatedPositions: number | undefined;

    constructor(
        private readonly parent: CartographicEditPointParentEntity,
        public readonly heightEntity: DraggableHeightEntity,
        startPosition: Cartesian3,
        endPosition: Cartesian3,
        polylineProps?: PolylineProps
    ) {
        super(heightEntity.parentEntityId, startPosition, endPosition, polylineProps);

        this._heightForElevatedPositions = heightEntity.height;
        this._elevatedPositions = [
            getElevatedCesiumPoint(startPosition, this._heightForElevatedPositions),
            getElevatedCesiumPoint(endPosition, this._heightForElevatedPositions),
        ];
    }

    public get entityId() {
        return this.heightEntity.parentEntityId;
    }

    public get props() {
        return (this.heightEntity.parent as EditablePrism).defaultPolylineProps;
    }

    public setStartPosition(position: Cartesian3): void {
        super.setStartPosition(position);

        this._heightForElevatedPositions = this.heightEntity.height;
        this._elevatedPositions = [
            getElevatedCesiumPoint(position, this._heightForElevatedPositions),
            getElevatedCesiumPoint(this.getEndPosition(), this._heightForElevatedPositions),
        ];
    }

    public setEndPosition(position: Cartesian3): void {
        super.setEndPosition(position);

        this._heightForElevatedPositions = this.heightEntity.height;
        this._elevatedPositions = [
            getElevatedCesiumPoint(this.getStartPosition(), this._heightForElevatedPositions),
            getElevatedCesiumPoint(position, this._heightForElevatedPositions),
        ];
    }

    public getElevatedPositionsCallbackProperty() {
        return new CallbackProperty(() => {
            if (!this._elevatedPositions || this._heightForElevatedPositions !== this.heightEntity.height) {
                this._heightForElevatedPositions = this.heightEntity.height;
                this._elevatedPositions = [
                    getElevatedCesiumPoint(this.getStartPosition(), this._heightForElevatedPositions),
                    getElevatedCesiumPoint(this.getEndPosition(), this._heightForElevatedPositions),
                ];
            }

            return this._elevatedPositions;
        }, this.parent.isStaticEntity);
    }
}

const HEIGHT_OUTLINE_ENTITY_ID = "heightOutline";
const BOTTOM_OUTLINE_ENTITY_ID = "bottomHeightOutline";
const SHADOW_ID_PREFIX = "_shadow";

export class EditablePrism implements HeightEditable, HeightPointsProvider, CartographicEditPointParentEntity {
    private _positions: CartographicEditPoint[] = [];
    private _polygonWaypoint: CartographicEditPoint | undefined;
    private _topHeightOutline: DraggableHeightEntity;
    private _bottomHeightOutline: DraggableHeightEntity;
    private movingPoint: CartographicEditPoint | undefined;
    private doneCreation = false;
    private isDisposed = false;
    private _enableEdit = true;
    private _prismProps!: PrismProps;
    private _defaultPointProps!: PointProps;
    private _defaultPolylineProps!: PolylineProps;
    private lastDraggedToPosition: Cartesian3 | undefined;
    private _labels: PrismLabel[] = [];
    private _hoverLabelId: PrismLabelKey | undefined = "waypoint";
    private _isValid = true;
    private positionsHierarchy = new PolygonHierarchy([]);
    private isPositionHierarchyValid = false;

    private polyline: PolylineWithHeight | undefined;

    private pointsDataSource = new CustomDataSource();
    private editPrismDataSource = new CustomDataSource();
    private polylineDataSource = new CustomDataSource();
    private polygonWaypointsDataSource = new CustomDataSource();

    public get isValid() {
        return this._isValid;
    }

    private get positions() {
        return this._positions;
    }

    private set positions(value: CartographicEditPoint[]) {
        this._positions = value;
    }

    constructor(
        private id: string,
        private readonly cesiumService: CesiumMapService,
        private prismOptions: PrismEditOptions,
        topHeight: number,
        bottomHeight: number,
        positions?: Cartesian3[]
    ) {
        this.prismProps = { ...prismOptions.prismProps };
        this.defaultPointProps = { ...prismOptions.pointProps };
        this.defaultPolylineProps = { ...prismOptions.polylineProps };

        this.cesiumService?.getViewer().dataSources.add(this.pointsDataSource);
        this.cesiumService?.getViewer().dataSources.add(this.editPrismDataSource);
        this.cesiumService?.getViewer().dataSources.add(this.polylineDataSource);
        this.cesiumService?.getViewer().dataSources.add(this.polygonWaypointsDataSource);

        this._topHeightOutline = new DraggableHeightEntity(
            this,
            this,
            HeightEntityType.Top,
            topHeight,
            this.prismProps.areHeightsDraggable
        );
        this._bottomHeightOutline = new DraggableHeightEntity(
            this,
            this,
            HeightEntityType.Bottom,
            bottomHeight,
            this.prismProps.areHeightsDraggable
        );

        if (positions && positions.length >= MINIMUM_VERTEX_NUMBER) {
            this.createFromExisting(positions);
        }
    }

    public get isStaticEntity() {
        return this.doneCreation;
    }

    private createOrUpdatePrismEntity() {
        let prismEntity = this.editPrismDataSource.entities.getById(this.id);
        const prismShadowEntity = this.editPrismDataSource.entities.getById(this.id + SHADOW_ID_PREFIX);

        if (!prismEntity || !prismShadowEntity) {
            prismEntity = this.editPrismDataSource.entities.add({
                id: this.id,
                polygon: {
                    hierarchy: this.getPositionsHierarchyCallbackProperty(),
                    material: this.getMaterial(),
                    fill: this.prismProps.fill,
                    classificationType: this.prismProps.classificationType,
                    heightReference: HeightReference.RELATIVE_TO_GROUND,
                    height: this.getBottomHeightCallbackProperty(),
                    show: this.isValidPolygon,
                    extrudedHeight: this.getTopHeightCallbackProperty(),
                },
            });

            prismEntity.acEntity = this;

            this.editPrismDataSource.entities.add({
                id: this.id + SHADOW_ID_PREFIX,
                polygon: {
                    hierarchy: this.getPositionsHierarchyCallbackProperty(),
                    material: this.prismProps.shadowMaterial,
                    fill: this.prismProps.fill,
                    show: this.isValidPolygon,
                    classificationType: this.prismProps.classificationType,
                    outline: true,
                    outlineColor: Color.WHITE,
                    heightReference: HeightReference.RELATIVE_TO_GROUND,
                    height: this.getBottomHeightCallbackProperty(),
                },
            });
        }
        if (!this.isPositionHierarchyValid) {
            this.recalculatePositionsHierarchy();
            this.isPositionHierarchyValid = true;
        }
    }

    public get heightConstraints() {
        return this.prismProps;
    }

    public shouldShowLabel(label: PrismLabel) {
        return label.show && (this._hoverLabelId === label.id || (!this.doneCreation && label.id === "waypoint"));
    }

    public set hoverLabelId(value: PrismLabelKey | undefined) {
        this._hoverLabelId = value;
    }

    public get hoverLabelId() {
        return this._hoverLabelId;
    }

    public get labels(): PrismLabel[] {
        return this._labels;
    }

    public set labels(value: PrismLabel[]) {
        if (value && value.length === 0) {
            this._labels = [];

            return;
        }

        const positions = this.getRealPositions();

        if (!value || !positions?.length) {
            return;
        }

        this._labels = value.map((label) => {
            switch (label.id) {
                case "topHeight":
                    label.position = getElevatedCesiumPoint(positions[0], this._topHeightOutline.height);
                    break;
                case "bottomHeight":
                    label.position = getElevatedCesiumPoint(positions[0], this._bottomHeightOutline.height);
                    break;
                case "waypoint":
                    label.position = this._polygonWaypoint?.getPosition() ?? positions[0];
                    break;
            }

            label.show = this.shouldShowLabel(label);
            label.position ??= Cartesian3.ZERO;

            return { ...DEFAULT_LABEL_PROPS, ...label };
        });
    }

    public get defaultPolylineProps(): PolylineProps {
        return this._defaultPolylineProps;
    }

    public set defaultPolylineProps(value: PolylineProps) {
        this._defaultPolylineProps = value;
    }

    public get prismProps(): PrismProps {
        return this._prismProps;
    }

    public set prismProps(value: PrismProps) {
        this._prismProps = value;
    }

    public get defaultPointProps(): PointProps {
        return this._defaultPointProps;
    }

    public set defaultPointProps(value: PointProps) {
        this._defaultPointProps = value;
    }

    public get enableEdit() {
        return this._enableEdit;
    }

    public set enableEdit(value: boolean) {
        this._enableEdit = value;
        this.positions.forEach((point) => {
            point.show = value;
        });
        this.updatePointsLayer(false, ...this.positions);
    }

    private createFromExisting(positions: Cartesian3[]) {
        positions.forEach((position) => {
            this.addPointFromExisting(position);
        });
        this.addAllVirtualEditPoints();
        this.updatePrismsLayer();
        this.updateHeightOutlinesLayer();
        this.doneCreation = true;
    }

    private isPoint(value: Cartesian3 | CesiumPointWithProps): value is CesiumPointWithProps {
        return !!(
            value as {
                pointProps: PointProps;
            }
        ).pointProps;
    }

    public setManually(
        points: CesiumPointWithProps[] | Cartesian3[],
        topHeight = 0,
        bottomHeight = 0,
        center?: Cartesian3,
        prismProps: PrismProps = this.prismProps
    ) {
        if (!this.doneCreation) {
            throw new Error("Update manually only in edit mode, after prism is created");
        }

        this.prismProps = { ...this.prismProps, ...prismProps };

        const newPoints: CartographicEditPoint[] = [];

        for (let i = 0; i < points.length; i++) {
            const pointOrCartesian = points[i];
            let newPoint = null;

            if (this.isPoint(pointOrCartesian)) {
                newPoint = new CartographicEditPoint(this, pointOrCartesian.position, pointOrCartesian.pointProps);
            } else {
                newPoint = new CartographicEditPoint(this, pointOrCartesian, this.defaultPointProps);
            }

            newPoints.push(newPoint);
        }

        this._topHeightOutline.setConstrainedHeight(topHeight);
        this._bottomHeightOutline.setConstrainedHeight(bottomHeight);

        if (center && this._polygonWaypoint) {
            const delta = GeoUtilsService.getPositionsDelta(this._polygonWaypoint.getPosition(), center);
            newPoints.forEach((point) => {
                const newPosition = GeoUtilsService.addDeltaToPosition(point.getPosition(), delta, true);
                point.setPosition(newPosition);
            });

            this._polygonWaypoint.setPosition(center);
        }

        this.positions = newPoints;
        this.updatePointsLayer(true, ...this.positions);
        this.addAllVirtualEditPoints();
        this.updatePrismsLayer();
        this.updateHeightOutlinesLayer();
    }

    private addAllVirtualEditPoints() {
        const currentPoints = [...this.positions];
        const midPoints: CartographicEditPoint[] = [];

        currentPoints.forEach((point, index) => {
            const currentPoint = point;
            const nextIndex = (index + 1) % currentPoints.length;
            const nextPoint = currentPoints[nextIndex];
            const midPoint = this.setMiddleVirtualPoint(currentPoint, nextPoint);
            midPoints.push(midPoint);
        });

        this.updatePointsLayer(false, ...midPoints);
    }

    private setMiddleVirtualPoint(firstPoint: CartographicEditPoint, secondPoint: CartographicEditPoint): CartographicEditPoint {
        const midPointCartesian3 = Cartesian3.lerp(
            firstPoint.getPosition(),
            secondPoint.getPosition(),
            // eslint-disable-next-line no-magic-numbers
            0.5,
            new Cartesian3()
        );

        const midPoint = new CartographicEditPoint(this, midPointCartesian3, this.defaultPointProps);
        midPoint.setVirtualEditPoint(true);

        const firstIndex = this.positions.indexOf(firstPoint);

        this.positions.splice(firstIndex + 1, 0, midPoint);

        return midPoint;
    }

    public getGroundPointForHeight() {
        return this.positions[0].getPosition();
    }

    private updateMiddleVirtualPoint(
        virtualEditPoint: CartographicEditPoint,
        prevPoint: CartographicEditPoint,
        nextPoint: CartographicEditPoint
    ) {
        // eslint-disable-next-line no-magic-numbers
        const midPointCartesian3 = Cartesian3.lerp(prevPoint.getPosition(), nextPoint.getPosition(), 0.5, new Cartesian3());
        virtualEditPoint.setPosition(midPointCartesian3);
    }

    public changeVirtualPointToRealPoint(point: CartographicEditPoint) {
        point.setVirtualEditPoint(false); // virtual point becomes a real point

        const pointsCount = this.positions.length;
        const pointIndex = this.positions.indexOf(point);
        const nextIndex = (pointIndex + 1) % pointsCount;
        const preIndex = (pointIndex - 1 + pointsCount) % pointsCount;

        const nextPoint = this.positions[nextIndex];
        const prePoint = this.positions[preIndex];

        const firstMidPoint = this.setMiddleVirtualPoint(prePoint, point);
        const secondMidPoint = this.setMiddleVirtualPoint(point, nextPoint);

        this.updatePointsLayer(true, firstMidPoint, secondMidPoint, point);
        this.updatePrismsLayer();
    }

    private updateHeightOutlinesLayer(points?: CartographicEditPoint[]) {
        this.isPositionHierarchyValid = false;
        this.polyline ??= new PolylineWithHeight();
        this.polyline.clear();

        const realPoints = points ?? this.positions.filter((position) => !position.isVirtualEditPoint());
        if (realPoints.length < 2) {
            return;
        }

        this.polyline.addPoint(realPoints[0].getPosition(), this._topHeightOutline.height, this._bottomHeightOutline.height);

        realPoints.forEach((point, index) => {
            const nextIndex = (index + 1) % realPoints.length;
            const nextPoint = realPoints[nextIndex];

            this.polyline?.addPoint(nextPoint.getPosition(), this._topHeightOutline.height, this._bottomHeightOutline.height);
        });

        const heightOutlineEntity = this.polylineDataSource.entities.getById(HEIGHT_OUTLINE_ENTITY_ID);
        const bottomOutlineEntity = this.polylineDataSource.entities.getById(BOTTOM_OUTLINE_ENTITY_ID);

        if (!heightOutlineEntity) {
            const { width, classificationType, material } = this.defaultPolylineProps;
            this.polylineDataSource.entities.add({
                id: HEIGHT_OUTLINE_ENTITY_ID,
                polyline: {
                    positions: this.polyline.getMaxElevatedPositionsCallbackProperty(),
                    width,
                    material,
                    classificationType,
                    heightReference: HeightReference.RELATIVE_TO_GROUND,
                } as unknown as PolylineGraphics,
            });
        } else if (heightOutlineEntity.polyline) {
            heightOutlineEntity.polyline.positions = this.polyline.getMaxElevatedPositionsCallbackProperty();
        }
        if (!bottomOutlineEntity) {
            const { width, classificationType, material } = this.defaultPolylineProps;
            this.polylineDataSource.entities.add({
                id: BOTTOM_OUTLINE_ENTITY_ID,
                polyline: {
                    positions: this.polyline.positionCallbackProperty(),
                    width,
                    material,
                    classificationType,
                    heightReference: HeightReference.RELATIVE_TO_GROUND,
                } as unknown as PolylineGraphics,
            });
        } else if (bottomOutlineEntity.polyline) {
            bottomOutlineEntity.polyline.positions = this.polyline.positionCallbackProperty();
        }
    }

    public addPointFromExisting(position: Cartesian3) {
        const newPoint = new CartographicEditPoint(this, position, this.defaultPointProps);

        this.positions.push(newPoint);
        this.updatePointsLayer(true, newPoint);
    }

    public addPoint(position: Cartesian3) {
        if (this.doneCreation) {
            return;
        }

        const isFirstPoint = !this.positions.length;

        if (isFirstPoint) {
            const firstPoint = new CartographicEditPoint(this, position, this.defaultPointProps);
            this.positions.push(firstPoint);
            this.updatePointsLayer(true, firstPoint);
        }

        this.movingPoint = new CartographicEditPoint(this, position, this.defaultPointProps);
        this.positions.push(this.movingPoint);

        this.updatePointsLayer(true, this.movingPoint);
        this.updatePrismsLayer();
        this.updateHeightOutlinesLayer();
    }

    public movePointFinish(editPoint: CartographicEditPoint) {
        if (this.prismOptions.clampHeightTo3D) {
            editPoint.props.disableDepthTestDistance = Number.POSITIVE_INFINITY;
            this.updatePointsLayer(false, editPoint);
        }
    }

    public updateSelfIntersectionsValidity(): void {
        this.checkSelfIntersections();
    }

    private checkSelfIntersections() {
        const truePositions = this.positions
            .filter((editPoint) => !editPoint.isVirtualEditPoint())
            .map((editPoint): Cartesian3 => editPoint.getPosition())
            .filter((editPosition, index, allPoints) => {
                for (let pointIndex = index; pointIndex > 0; pointIndex--) {
                    if (editPosition.equals(allPoints[pointIndex - 1])) {
                        return false;
                    }
                }

                return true;
            })
            .map((editPosition): Position => {
                const { x, y, z } = editPosition;

                return [x, y, z];
            });

        this._isValid = checkPolygonEdgesIntersections(truePositions);
    }

    private checkSelfIntersectionsWithoutSpecficPoint(pointToRemove: CartographicEditPoint) {
        const truePositions = this.positions
            .filter((editPoint) => !editPoint.isVirtualEditPoint() && editPoint !== pointToRemove)
            .map((editPoint): Position => {
                const { x, y, z } = editPoint.getPosition();

                return [x, y, z];
            });

        return checkPolygonEdgesIntersections(truePositions);
    }

    public movePoint(toPosition: Cartesian3, editPoint: CartographicEditPoint) {
        editPoint.setPosition(toPosition);
        this.checkSelfIntersections();

        if (!this.doneCreation) {
            this.updatePrismsLayer();
            this.updatePointsLayer(true, editPoint);

            return;
        }

        if (editPoint.props.disableDepthTestDistance && this.prismOptions.clampHeightTo3D) {
            editPoint.props.disableDepthTestDistance = undefined;

            return;
        }

        if (editPoint.isVirtualEditPoint()) {
            this.changeVirtualPointToRealPoint(editPoint);
        }

        const pointsCount = this.positions.length;
        const pointIndex = this.positions.indexOf(editPoint);
        const nextVirtualPoint = this.positions[(pointIndex + 1) % pointsCount];
        const nextRealPoint = this.positions[(pointIndex + 2) % pointsCount];
        const prevVirtualPoint = this.positions[(pointIndex - 1 + pointsCount) % pointsCount];
        const prevRealPoint = this.positions[(pointIndex - 2 + pointsCount) % pointsCount];
        this.updateMiddleVirtualPoint(nextVirtualPoint, editPoint, nextRealPoint);
        this.updateMiddleVirtualPoint(prevVirtualPoint, editPoint, prevRealPoint);

        this.updatePrismsLayer();
        this.updatePointsLayer(true, editPoint);
    }

    public moveTempMovingPoint(toPosition: Cartesian3) {
        if (this.movingPoint) {
            this.movePoint(toPosition, this.movingPoint);
        } else {
            this.movingPoint = new CartographicEditPoint(this, toPosition, this.defaultPointProps);
            this.positions.push(this.movingPoint);
        }
    }

    public removeTempMovingPoint() {
        if (this.movingPoint) {
            this.removePosition(this.movingPoint);
            this.movingPoint = undefined;
            this.updatePrismsLayer();
            this.updatePointsLayer(true);
        }
    }

    public movePrism(startMovingPosition: Cartesian3, draggedToPosition: Cartesian3) {
        if (!this.doneCreation) {
            return;
        }

        if (!this.lastDraggedToPosition) {
            this.lastDraggedToPosition = startMovingPosition;
        }

        const delta = GeoUtilsService.getPositionsDelta(this.lastDraggedToPosition, draggedToPosition);
        this.positions.forEach((point) => {
            const newPosition = GeoUtilsService.addDeltaToPosition(point.getPosition(), delta, true);
            point.setPosition(newPosition);
        });

        this.recalculatePositionsHierarchy();

        if (this._polygonWaypoint) {
            const newPosition = GeoUtilsService.addDeltaToPosition(this._polygonWaypoint.getPosition(), delta, true);
            this._polygonWaypoint.setPosition(newPosition);
        }

        this.lastDraggedToPosition = draggedToPosition;
        this.updatePointsLayer(true, ...this.positions);
    }

    public endMovePrism() {
        this.lastDraggedToPosition = undefined;
        this.updatePointsLayer(true, ...this.positions);
    }

    public moveTopHeight(newHeight: number) {
        this._topHeightOutline.setConstrainedHeight(newHeight);

        this.updatePointsLayer(true, ...this.positions);
        this.updateHeightOutlinesLayer();
    }

    public moveBottomHeight(newHeight: number) {
        this._bottomHeightOutline.setConstrainedHeight(newHeight);

        this.updatePointsLayer(true, ...this.positions);
        this.updateHeightOutlinesLayer();
    }

    public tryRemovePoint(pointToRemove: CartographicEditPoint, shouldRefreshVirtualPoints: boolean) {
        if (!this.checkSelfIntersectionsWithoutSpecficPoint(pointToRemove)) {
            return;
        }

        this.removePosition(pointToRemove);
        this.positions.filter((point) => point.isVirtualEditPoint()).forEach((point) => this.removePosition(point));

        if (shouldRefreshVirtualPoints) {
            this.addAllVirtualEditPoints();
        }

        this.updateHeightOutlinesLayer();
        this.updatePrismsLayer();
        this.refreshPolygonWaypoint(this.getRealPoints());
    }

    public finishCreation() {
        this.doneCreation = true;
        this.removePosition(this.movingPoint);
        this.movingPoint = undefined;
        this.updatePrismsLayer();

        this.addAllVirtualEditPoints();
        this.updateHeightOutlinesLayer();
    }

    public getRealPositions(): Cartesian3[] {
        return this.getRealPoints().map((position) => position.getPosition());
    }

    public getRealPoints(): CartographicEditPoint[] {
        return this.positions.filter((position) => !position.isVirtualEditPoint() && position !== this.movingPoint);
    }

    public getPoints(): CartographicEditPoint[] {
        return this.positions.filter((position) => position !== this.movingPoint);
    }

    public getTopHeight(): number {
        return this._topHeightOutline.height;
    }

    public getBottomHeight(): number {
        return this._bottomHeightOutline.height;
    }

    public getPolygonWaypoint() {
        return this._polygonWaypoint;
    }

    public getTopHeightCallbackProperty() {
        return new CallbackProperty(this.getTopHeight.bind(this), this.isStaticEntity);
    }

    public getBottomHeightCallbackProperty() {
        return new CallbackProperty(this.getBottomHeight.bind(this), this.isStaticEntity);
    }

    private recalculatePositionsHierarchy() {
        this.positionsHierarchy.positions = this.optimizedGetPositionsHierarchy();
    }
    public getPositionsHierarchyCallbackProperty() {
        return new CallbackProperty(() => this.positionsHierarchy, true);
    }

    private optimizedGetPositionsHierarchy() {
        const result = [];
        const length = this.positions.length;
        for (let index = 0; index < length; index++) {
            const position = this.positions[index];
            if (!position.isVirtualEditPoint()) {
                result.push(position.getPosition());
            }
        }

        return result;
    }

    public getStatus(): EntityStatus {
        const realPoints = this.getRealPoints();

        return {
            isFinished: this.doneCreation,
            canFinish: realPoints.length >= MINIMUM_VERTEX_NUMBER,
            canRemovePreviousPoint: realPoints.length > 1,
        };
    }

    public get isValidPolygon() {
        // NOTE: this workaround is needed because of https://github.com/CesiumGS/cesium/issues/7950 issue
        // we allow drawing polygon graphics only when there are minimum 3 vertices (valid polygons)
        return this.getRealPointsCount() >= MINIMUM_VERTEX_NUMBER;
    }

    private getMaterialCallbackProperty() {
        return this._isValid ? this.prismProps.material : this.prismProps.invalidMaterial;
    }

    public getMaterial() {
        const materialProperty = new ColorMaterialProperty();
        materialProperty.color = new CallbackProperty(this.getMaterialCallbackProperty.bind(this), this.isStaticEntity);

        return materialProperty;
    }

    private removePosition(point: CartographicEditPoint | undefined) {
        const index = this.positions.findIndex((position) => position === point);
        if (!point || index < 0) {
            return;
        }

        this.positions.splice(index, 1);
        this.pointsDataSource.entities.removeById(point.getId());
    }

    private updatePrismsLayer() {
        this.isPositionHierarchyValid = false;

        if (this.getRealPointsCount() >= MINIMUM_VERTEX_NUMBER) {
            this.createOrUpdatePrismEntity();
        }
    }

    private updatePointsLayer(shouldRenderPolylines: boolean, ...points: CartographicEditPoint[]) {
        this.isPositionHierarchyValid = false;

        this.pointsDataSource.entities.values
            .filter(({ id }: Entity) => !this.positions.some((point) => point.getId() === id))
            .forEach(({ id }: { id: string }) => {
                this.pointsDataSource.entities.removeById(id);
            });

        points.forEach((point) => {
            const pointEntity = this.pointsDataSource.entities.getOrCreateEntity(point.getId());
            pointEntity.position = point.getPositionCallbackProperty() as unknown as PositionProperty;
            pointEntity.point ??= {
                pixelSize: this.getPointSize(point),
                color: point.props.color,
                outlineColor: point.props.outlineColor,
                outlineWidth: point.props.outlineWidth,
                show: this.getPointShow(point),
                disableDepthTestDistance: point.props.disableDepthTestDistance,
                heightReference: point.props.heightReference,
                zIndex: 2,
            } as unknown as PointGraphics;
            pointEntity.acEntity ??= point;
        });

        const polygonPoints = this.getRealPoints();

        if (!this.doneCreation && this.movingPoint) {
            polygonPoints.push(this.movingPoint);
        }

        if (shouldRenderPolylines) {
            this.updateHeightOutlinesLayer(polygonPoints);
        }

        if (
            polygonPoints.length < MINIMUM_VERTEX_NUMBER ||
            polygonPoints[polygonPoints.length - 2].getPosition().equals(polygonPoints[polygonPoints.length - 1].getPosition())
        ) {
            return;
        }

        this.refreshPolygonWaypoint(polygonPoints);
    }

    private refreshPolygonWaypoint(points: CartographicEditPoint[]) {
        this.isPositionHierarchyValid = false;

        const visualCenter = getPolygonVisualCenter(points.map((point) => point.getCartographic()));
        const position = convertSerializableCartographicToCartesian3(visualCenter);

        if (this._polygonWaypoint) {
            this._polygonWaypoint.setPosition(position);
        } else {
            this._polygonWaypoint = new CartographicEditPoint(this, position, this.defaultPointProps);
        }

        const pointEntity = this.pointsDataSource.entities.getOrCreateEntity(this._polygonWaypoint.getId());
        pointEntity.position = this._polygonWaypoint.getPositionCallbackProperty() as unknown as PositionProperty;
        pointEntity.point ??= {
            pixelSize: this.getPointSize(this._polygonWaypoint),
            color: this._polygonWaypoint.props.color,
            outlineColor: this._polygonWaypoint.props.outlineColor,
            outlineWidth: this._polygonWaypoint.props.outlineWidth,
            show: this.getPointShow(this._polygonWaypoint),
            disableDepthTestDistance: this._polygonWaypoint.props.disableDepthTestDistance,
            heightReference: HeightReference.NONE,
        } as unknown as PointGraphics;
    }

    public dispose() {
        this.isDisposed = true;

        if (this.movingPoint) {
            this.movingPoint = undefined;
        }

        if (this._polygonWaypoint) {
            this._polygonWaypoint = undefined;
        }

        this.pointsDataSource.entities.removeAll();
        this.editPrismDataSource.entities.removeAll();
        this.polylineDataSource.entities.removeAll();
        this.polygonWaypointsDataSource.entities.removeAll();

        this.positions.length = 0;
        this.isPositionHierarchyValid = false;
    }

    public getRealPointsCount(): number {
        return ArrayUtils.unique(
            this.positions.filter((position) => !position.isVirtualEditPoint()),
            (item) => item.getPosition().toString()
        ).length;
    }

    public getId() {
        return this.id;
    }

    public getPointSize(point: CartographicEditPoint) {
        return point.isVirtualEditPoint() ? point.props.virtualPointPixelSize : point.props.pixelSize;
    }

    public getPointShow(point: CartographicEditPoint) {
        return point.show && (point.isVirtualEditPoint() ? point.props.showVirtual : point.props.show);
    }

    public hide() {
        this.pointsDataSource.entities.show = false;
        this.editPrismDataSource.entities.show = false;
        this.polylineDataSource.entities.show = false;
        this.polygonWaypointsDataSource.entities.show = false;
        this.cesiumService.getScene().requestRender();
    }

    public show() {
        this.pointsDataSource.entities.show = true;
        this.editPrismDataSource.entities.show = true;
        this.polylineDataSource.entities.show = true;
        this.polygonWaypointsDataSource.entities.show = true;
        this.cesiumService.getScene().requestRender();
    }
}
